import { Shield, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { usePriceMatchPolicy } from "@/hooks/usePriceMatchPolicy";
import { Button } from "@/components/ui/button";

export function PriceMatchSection() {
  const { policy, shouldShow: shouldShowConsumables } = usePriceMatchPolicy("consumable");
  const { shouldShow: shouldShowEquipment } = usePriceMatchPolicy("equipment");

  if (!policy?.enabled || (!shouldShowConsumables && !shouldShowEquipment)) return null;

  const message = policy.message.replace("{discount}", String(policy.discountPercentage));
  const ctaLink = shouldShowConsumables ? "/consumiveis" : "/equipamentos";

  return (
    <section className="py-10 md:py-14 bg-gradient-to-br from-success/10 via-success/5 to-background relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-success/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-success/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

      <div className="container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto text-center"
        >
          {/* Animated Shield */}
          <motion.div
            initial={{ scale: 0.8 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-flex items-center justify-center mb-6"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-success/20 rounded-full blur-xl animate-pulse" />
              <div className="relative p-5 rounded-full bg-gradient-to-br from-success/20 to-success/10 border-2 border-success/30">
                <Shield className="h-10 w-10 text-success" />
              </div>
            </div>
          </motion.div>

          {/* Title */}
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Garantia Melhor Preço
            <span className="block text-success-text mt-1">da Europa</span>
          </h2>

          {/* Discount highlight */}
          <div className="inline-flex items-center gap-2 bg-success/15 border border-success/30 rounded-full px-6 py-2.5 mb-6">
            <span className="text-success-text font-bold text-2xl md:text-3xl">
              +{policy.discountPercentage}%
            </span>
            <span className="text-success-text font-medium text-sm md:text-base">
              desconto adicional
            </span>
          </div>

          {/* Message */}
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
            {message}
          </p>

          {/* CTA */}
          <Button size="lg" asChild className="bg-success hover:bg-success/90 text-success-foreground">
            <Link to={ctaLink}>
              Explorar Produtos
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>

          {/* Trust indicators */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
            {shouldShowConsumables && (
              <span className="flex items-center gap-1.5">
                <Shield className="h-4 w-4 text-success" />
                Consumíveis
              </span>
            )}
            {shouldShowEquipment && (
              <span className="flex items-center gap-1.5">
                <Shield className="h-4 w-4 text-success" />
                Equipamentos
              </span>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
